import React, { useState, useEffect } from 'react';
import { adminApi } from '../api/adminApi';
import toast, { Toaster } from 'react-hot-toast';
import { PaperAirplaneIcon, MegaphoneIcon, EyeIcon, XMarkIcon } from '@heroicons/react/24/outline';
import PremiumTable from '../components/common/PremiumTable';
import PageHeader from '../components/common/PageHeader';

const Broadcast = () => {
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [audience, setAudience] = useState('all');
  const [loading, setLoading] = useState(false);
  
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(true);

  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [readers, setReaders] = useState([]);
  const [selectedTitle, setSelectedTitle] = useState('');

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      setHistoryLoading(true);
      const res = await adminApi.getBroadcastHistory();
      setHistory(res.data.data || []);
    } catch (error) {
      console.error('Error fetching broadcast history:', error);
      toast.error('Failed to load history');
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!subject || !message) {
      toast.error('Subject and message are required');
      return;
    }

    try {
      setLoading(true);
      const res = await adminApi.sendBroadcast({ subject, message, audience });
      toast.success(res.data.message);
      setSubject('');
      setMessage('');
      fetchHistory(); // Refresh table
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to send broadcast');
    } finally {
      setLoading(false);
    }
  };

  const handleViewReaders = async (broadcast) => {
    setSelectedTitle(broadcast.title);
    setIsModalOpen(true);
    setModalLoading(true);
    setReaders([]);
    
    try {
      const res = await adminApi.getBroadcastReaders(broadcast._id);
      setReaders(res.data.data || []);
    } catch (error) {
      toast.error('Failed to fetch readers list');
    } finally {
      setModalLoading(false);
    }
  };

  const columns = [
    {
      header: 'Subject',
      accessor: 'title',
      render: (row) => (
        <div className="flex items-center gap-3">
          <div className="p-2 bg-red-50 rounded-lg border border-red-100 shadow-sm">
            <MegaphoneIcon className="h-5 w-5 text-red-500" />
          </div>
          <div>
            <div className="text-sm font-bold text-gray-900">{row.title}</div>
            <div className="text-xs text-gray-500 max-w-[200px] truncate" title={row.message}>{row.message}</div>
          </div>
        </div>
      )
    },
    {
      header: 'Audience',
      accessor: 'targetAudience',
      render: (row) => {
        let badgeClass = 'bg-gray-100 text-gray-800';
        if (row.targetAudience === 'premium') badgeClass = 'bg-purple-100 text-purple-800';
        if (row.targetAudience === 'active') badgeClass = 'bg-green-100 text-green-800';
        return (
          <span className={`px-2.5 py-1 inline-flex text-[11px] leading-5 font-bold rounded-full uppercase tracking-wider ${badgeClass}`}>
            {row.targetAudience}
          </span>
        );
      }
    },
    {
      header: 'Date & Time',
      accessor: 'createdAt',
      render: (row) => (
        <div>
          <div className="text-sm font-medium text-gray-900">
            {new Date(row.createdAt).toLocaleDateString()}
          </div>
          <div className="text-xs text-gray-500">
            {new Date(row.createdAt).toLocaleTimeString()}
          </div>
        </div>
      )
    },
    {
      header: 'Actions',
      render: (row) => (
        <button
          onClick={() => handleViewReaders(row)}
          className="inline-flex items-center px-3 py-1.5 bg-gray-50 hover:bg-gray-100 text-gray-700 border border-gray-200 rounded-lg text-xs font-bold transition-colors shadow-sm"
        >
          <EyeIcon className="h-4 w-4 mr-1.5 text-gray-500" />
          View Readers ({row.readBy ? row.readBy.length : 0})
        </button>
      )
    }
  ];

  return (
    <div className="min-h-screen bg-transparent pb-12">
      <Toaster position="top-right" />
      
      <PageHeader 
        title="Broadcast Notifications"
        subtitle="Send announcements to users and track who has read them."
      />

      <main className="max-w-7xl space-y-8">
        {/* Send Broadcast Form */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden max-w-3xl">
          <form onSubmit={handleSubmit} className="p-8 space-y-6">
            
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Target Audience</label>
              <select
                value={audience}
                onChange={(e) => setAudience(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border outline-none transition-colors"
              >
                <option value="all">All Users (Registered)</option>
                <option value="active">Active Users Only</option>
                <option value="premium">Premium/Paid Users Only</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Subject / Title</label>
              <input
                type="text"
                value={subject}
                onChange={(e) => setSubject(e.target.value)}
                placeholder="e.g. Scheduled Maintenance Notice"
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border outline-none transition-colors"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Message Content</label>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={6}
                placeholder="Write your announcement here..."
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-red-500 focus:ring-red-500 sm:text-sm p-2.5 border outline-none transition-colors"
                required
              />
              <p className="text-xs text-gray-500 mt-2">This message will instantly appear in the users' notification bell.</p>
            </div>

            <div className="pt-4 border-t border-gray-100 flex justify-end">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center justify-center px-6 py-2.5 border border-transparent rounded-full shadow-sm text-sm font-bold text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500 disabled:opacity-50 transition-all"
              >
                {loading ? 'Sending...' : (
                  <>
                    <PaperAirplaneIcon className="h-4 w-4 mr-2" />
                    Send Broadcast
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Broadcast History Table */}
        {historyLoading ? (
          <div className="p-8 text-center text-gray-500">Loading history...</div>
        ) : (
          <PremiumTable 
            title="Broadcast History"
            data={history}
            columns={columns}
            searchKeys={['title', 'message', 'targetAudience']}
            primaryKey="_id"
            paginationMode="standard"
            itemsPerPage={10}
          />
        )}
      </main>

      {/* Readers Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-sm">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden flex flex-col max-h-[80vh]">
            <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-gray-50/50">
              <div>
                <h3 className="text-lg font-bold text-gray-900">Read Receipts</h3>
                <p className="text-xs text-gray-500 truncate max-w-[300px]">{selectedTitle}</p>
              </div>
              <button 
                onClick={() => setIsModalOpen(false)}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>
            </div>
            
            <div className="p-0 overflow-y-auto flex-1">
              {modalLoading ? (
                <div className="p-8 text-center text-gray-500 text-sm">Fetching readers...</div>
              ) : readers.length === 0 ? (
                <div className="p-8 text-center flex flex-col items-center">
                  <div className="h-12 w-12 bg-gray-50 rounded-full flex items-center justify-center mb-3">
                    <EyeIcon className="h-6 w-6 text-gray-400" />
                  </div>
                  <p className="text-gray-900 font-medium">No one has read this yet</p>
                  <p className="text-sm text-gray-500">When users open their notifications, they will appear here.</p>
                </div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {readers.map((r) => (
                    <li key={r.user?._id || Math.random()} className="px-6 py-4 hover:bg-gray-50 transition-colors flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <div className="h-10 w-10 rounded-full bg-gradient-to-tr from-gray-100 to-gray-200 flex items-center justify-center text-gray-600 font-bold shadow-sm">
                          {r.user?.name ? r.user.name.charAt(0).toUpperCase() : 'U'}
                        </div>
                        <div>
                          <p className="text-sm font-bold text-gray-900">{r.user?.name || 'Unknown User'}</p>
                          <p className="text-xs text-gray-500">{r.user?.email || 'No email'}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <span className="text-xs font-medium text-gray-400 bg-gray-100 px-2 py-1 rounded-md capitalize inline-block mb-1">
                          {r.user?.businessType || 'User'}
                        </span>
                        <div className="text-[10px] text-gray-400 font-medium">
                          {new Date(r.readAt).toLocaleString()}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Broadcast;
